/**
 * THE PRACTICE FORMS TAB — `tasks/patient-forms.md` §5.1 / §5.2 / §13.2.
 *
 * A list of the practice's forms, and an editor for one of them. The editor is three things stacked:
 * delivery settings (the form's metadata and flags), the appointment-type triggers, and the version
 * history — with `<app-custom-form-builder>` between them doing the questions and the preview.
 *
 * ── WHAT THIS COMPONENT IS CAREFUL ABOUT ─────────────────────────────────────────────────────
 *
 * **`schema` never leaves here on a save.** It cannot: `CustomFormService.updateForm` takes a typed
 * metadata shape with no `schema` in it, and the questions travel only through the builder's publish
 * call. §14.1 decision 4 makes that a 400 rather than a silent drop, which is exactly the kind of
 * refusal a form that "saved successfully and changed nothing" would otherwise hide.
 *
 * **A delete that is refused is shown, not swallowed.** 409 `CUSTOM_FORM_HAS_OPEN_REQUESTS` means
 * patients are mid-form. `details.openRequestCount` says how many, and the practice needs that
 * number to decide between revoking those requests and archiving the form instead. A toast that
 * says "could not delete" is not an answer to that question.
 *
 * **Triggers are listed one row per (appointment type, sendOn) pair**, so unlinking one passes
 * `?sendOn=` — §14.1 decision 1: without it the endpoint removes BOTH triggers for the pair, and a
 * practice removing the reminder trigger would silently lose the booking one it never touched.
 * `CustomFormService.unlinkAppointmentType` makes the parameter mandatory so that cannot be
 * forgotten here.
 *
 * **Link is upsert-shaped**, not create-or-fail (§14.1 decision 8): 201 created, 200
 * reactivated-or-already-linked. A paused row still occupies the unique slot, so all three outcomes
 * are normal and each gets its own message rather than an error.
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, firstValueFrom, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseComponent } from '../../../core/components/base/base.component';
import { AppointmentTypeService } from '../../../core/services/appointment-type.service';
import { FeedbackService } from '../../../core/services/feedback.service';
import { GroupService } from '../../../core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import {
  CustomFormDetailDto, CustomFormDto, CustomFormImportDto, CustomFormLinkDto, CustomFormMetadata,
  CustomFormSendOn, CustomFormService, CustomFormStatus, CustomFormVersionDto,
} from '../../../core/services/custom-form.service';
import { FormSchema } from '../../../shared/models/custom-form.model';
import { ImportedDraftNotice } from './custom-form-builder/custom-form-builder.component';
import {
  CustomFormImportDialogComponent, CustomFormImportDialogData, CustomFormImportDialogResult,
} from './custom-form-import/custom-form-import-dialog.component';

/**
 * One row of the list.
 *
 * ── AN AWKWARDNESS OF §13.2 ROW 1, NAMED RATHER THAN HIDDEN ─────────────────────────────────
 * `GET /custom-forms` returns `currentVersionId` but no `currentVersionNumber`, and no count of the
 * appointment types a form triggers on. §5.1 asks the list to show both. So each row is completed by
 * a `GET /custom-forms/:id/versions` and a `GET /custom-forms/:id/appointment-types`, forkJoin-ed —
 * two extra requests per form to draw one line of text. The version history is at least the cheap
 * read (§14.1 decision 2 keeps schemas out of it). Reported: `currentVersionNumber` and
 * `appointmentTypeCount` on the list DTO would remove both.
 */
export interface CustomFormRow {
  form: CustomFormDto;
  currentVersionNumber: number | null;
  versionCount: number;
  triggerCount: number;
}

@Component({
  selector: 'app-custom-form-settings',
  templateUrl: './custom-form-settings.component.html',
  styleUrls: ['./custom-form-settings.component.scss'],
})
export class CustomFormSettingsComponent extends BaseComponent implements OnInit {

  selectedGroup: any = null;

  rows: CustomFormRow[] = [];
  isLoading = false;
  listError: string | null = null;

  /** The refusal from the last delete attempt, kept on screen — a toast is too short for a count. */
  deleteBlocked: { formName: string; openRequestCount: number; message: string } | null = null;

  /* ── The editor ───────────────────────────────────────────────────────────────────────── */

  /** The form being edited, with its current version. `null` means the list is showing. */
  selectedForm: CustomFormDetailDto | null = null;

  /** What the builder starts from — a version's schema, or null for a form with none. */
  builderSchema: FormSchema | null = null;

  /** True when the builder holds questions that are not in any published version. */
  builderDirty = false;

  /**
   * Set only while the builder holds a schema that came out of a PDF (§16).
   *
   * Cleared by everything that replaces those questions with something else — closing the editor,
   * copying a version in, publishing — because the banner it drives says "unreviewed draft, not
   * published", and each of those makes that sentence false.
   */
  importedDraftNotice: ImportedDraftNotice | null = null;

  /** §16.1 — every attempt on this form, so a failed one is visible rather than silently gone. */
  imports: CustomFormImportDto[] = [];
  isLoadingImports = false;

  settingsForm!: FormGroup;
  isSavingSettings = false;

  versions: CustomFormVersionDto[] = [];
  currentVersionId: string | null = null;
  isLoadingVersions = false;

  /** A version opened from the history, read-only. */
  viewedVersion: CustomFormVersionDto | null = null;
  viewedSchema: FormSchema | null = null;
  isLoadingVersion = false;

  links: CustomFormLinkDto[] = [];
  appointmentTypes: any[] = [];
  linkAppointmentTypeId: string | null = null;
  linkSendOn: CustomFormSendOn = 'booking';
  isLinking = false;

  /* ── Creating ─────────────────────────────────────────────────────────────────────────── */

  isCreating = false;
  newFormName = '';

  readonly statuses: { value: CustomFormStatus; label: string; hint: string }[] = [
    { value: 'draft', label: 'Draft', hint: 'Not sent to anybody.' },
    { value: 'published', label: 'Published', hint: 'In use.' },
    { value: 'archived', label: 'Archived', hint: 'Kept, but not sent.' },
  ];

  readonly sendOns: { value: CustomFormSendOn; label: string }[] = [
    { value: 'booking', label: 'When the appointment is booked' },
    { value: 'reminder', label: 'With the appointment reminder' },
  ];

  /** A state unit (tasks/state-mode.md): forms are never sent to patients. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private formBuilder: FormBuilder,
    private customFormService: CustomFormService,
    private appointmentTypeService: AppointmentTypeService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.settingsForm = this.buildSettingsForm(null);

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (group: any) => {
          this.selectedGroup = group;
          if (!group) return;
          this.closeEditor(true);
          this.loadForms();
          this.loadAppointmentTypes();
        },
        error: () => {
          this.isLoading = false;
          this.listError = 'Your practice could not be loaded, so forms cannot be shown.';
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The list
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  loadForms(): void {
    this.isLoading = true;
    this.listError = null;

    this.customFormService.listForms()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const forms = response.data || [];
          if (forms.length === 0) {
            this.rows = [];
            this.isLoading = false;
            return;
          }
          this.completeRows(forms);
        },
        error: (error) => {
          this.isLoading = false;
          this.rows = [];
          this.listError = this.messageFor(error, 'The practice\'s forms could not be loaded.');
        },
      });
  }

  /**
   * Fill in the two facts the list DTO does not carry (see `CustomFormRow`).
   *
   * Each per-form read is `catchError`-ed to a neutral row rather than allowed to fail the whole
   * list: one form whose triggers cannot be read must not blank the settings page.
   */
  private completeRows(forms: CustomFormDto[]): void {
    const requests: Observable<CustomFormRow>[] = forms.map((form) => forkJoin({
      versions: this.customFormService.listVersions(form.id).pipe(
        catchError(() => of({ success: true, data: [], total: 0, currentVersionId: null })),
      ),
      links: this.customFormService.listAppointmentTypeLinks(form.id).pipe(
        catchError(() => of({ success: true, data: [], total: 0 })),
      ),
    }).pipe(
      map(({ versions, links }) => {
        const current = (versions.data || [])
          .filter((v) => v.id === form.currentVersionId)[0];
        return {
          form,
          currentVersionNumber: current ? current.versionNumber : null,
          versionCount: versions.data ? versions.data.length : 0,
          // Only ACTIVE links count as triggers: §14.1 decision 9 pauses rather than deletes them,
          // and a paused row is configuration, not a trigger that will fire.
          triggerCount: (links.data || []).filter((l) => l.active).length,
        } as CustomFormRow;
      }),
    ));

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.rows = rows;
          this.isLoading = false;
        },
        error: (error) => {
          // The metadata is still worth showing even if every enrichment failed.
          this.rows = forms.map((form) => ({
            form, currentVersionNumber: null, versionCount: 0, triggerCount: 0,
          }));
          this.isLoading = false;
          this.listError = this.messageFor(error,
            'Some form details could not be loaded. The list below may be incomplete.');
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Create / duplicate / delete
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  startCreate(): void {
    this.isCreating = true;
    this.newFormName = '';
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.newFormName = '';
  }

  createForm(): void {
    const name = (this.newFormName || '').trim();
    if (!name) {
      this.showToastMessage('Give the form a name first.');
      return;
    }

    // §14.1 decision 5: a create always lands in `draft` whatever the body says.
    this.customFormService.createForm({ name })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isCreating = false;
          this.newFormName = '';
          this.showToastMessage(`"${response.data.name}" created as a draft.`);
          this.loadForms();
          this.openForm(response.data.id);
        },
        error: (error) => {
          this.showToastMessage(this.messageFor(error, 'The form could not be created.'));
        },
      });
  }

  /**
   * DUPLICATE.
   *
   * The copy is created with the source's metadata and then opened with the source's questions
   * loaded into the builder — as a DRAFT with no version. That is not a shortcut: a schema exists
   * only on a version (§13.2), so a copy that had one already would have had to be published on the
   * practice's behalf, silently putting a form they have not read into a sendable state.
   */
  async duplicateForm(row: CustomFormRow): Promise<void> {
    const source = row.form;
    const metadata: CustomFormMetadata & { name: string } = {
      name: `Copy of ${source.name}`.slice(0, 300),
      description: source.description,
      category: source.category,
      sendWithInvite: false,          // a copy must not start sending itself
      allowManualSend: source.allowManualSend,
      requiresSignature: source.requiresSignature,
      guardianMayComplete: !!source.guardianMayComplete,
      expiryDays: source.expiryDays,
    };

    let sourceSchema: FormSchema | null = null;
    if (source.currentVersionId) {
      try {
        const version = await firstValueFrom(
          this.customFormService.getVersion(source.currentVersionId));
        sourceSchema = (version && version.data && version.data.schema) || null;
      } catch {
        this.showToastMessage(
          'The original form\'s questions could not be read; the copy will start empty.');
      }
    }

    this.customFormService.createForm(metadata)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showToastMessage(
            `"${response.data.name}" created as a draft. Publish it to create version 1.`);
          this.loadForms();
          this.openForm(response.data.id, sourceSchema);
        },
        error: (error) => {
          this.showToastMessage(this.messageFor(error, 'The form could not be duplicated.'));
        },
      });
  }

  /**
   * DELETE — soft, and refusable.
   *
   * A 409 carries `details.openRequestCount`. It is put on screen and left there, because the number
   * is the whole content of the answer: it tells the practice how many patients they would have to
   * chase before this form can go, and archiving instead is usually the right call.
   */
  async deleteForm(row: CustomFormRow): Promise<void> {
    this.deleteBlocked = null;

    const confirmed = await this.showConfirmationDialog(
      'Delete form',
      `Delete "${row.form.name}"? Forms patients have already completed still resolve it, and the `
      + 'published versions are kept.',
      undefined, 'Cancel', 'Delete', true);
    if (!confirmed) return;

    this.customFormService.deleteForm(row.form.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage(`"${row.form.name}" deleted.`);
          if (this.selectedForm && this.selectedForm.id === row.form.id) this.closeEditor(true);
          this.loadForms();
        },
        error: (error) => {
          const api = CustomFormService.asApiError(error);
          if (api && api.error === 'CUSTOM_FORM_HAS_OPEN_REQUESTS') {
            this.deleteBlocked = {
              formName: row.form.name,
              openRequestCount: (api.details && api.details.openRequestCount) || 0,
              message: api.message,
            };
            this.showToastMessage(api.message);
            return;
          }
          this.showToastMessage(this.messageFor(error, 'The form could not be deleted.'));
        },
      });
  }

  dismissDeleteBlocked(): void {
    this.deleteBlocked = null;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The editor
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Open one form.
   *
   * `GET /custom-forms/:id` returns the current version WITH its schema (§14.1 decision 10 re-queries
   * it group-scoped), so opening a published form seeds the builder with exactly the questions
   * patients are being asked today — which is what makes "edit and republish" a real edit rather
   * than starting again.
   */
  openForm(id: string, seedSchema: FormSchema | null = null): void {
    this.viewedVersion = null;
    this.viewedSchema = null;
    this.deleteBlocked = null;

    this.customFormService.getForm(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.selectedForm = response.data;
          this.settingsForm = this.buildSettingsForm(response.data);
          this.builderSchema = seedSchema
            || (response.data.currentVersion ? response.data.currentVersion.schema || null : null);
          this.builderDirty = false;
          this.importedDraftNotice = null;
          this.loadVersions(id);
          this.loadLinks(id);
          this.loadImports(id);
        },
        error: (error) => {
          this.showToastMessage(this.messageFor(error, 'That form could not be opened.'));
        },
      });
  }

  async closeEditor(force = false): Promise<void> {
    if (!force && this.builderDirty) {
      const confirmed = await this.showConfirmationDialog(
        'Leave without publishing?',
        'The questions you have changed have not been published, so patients would still receive '
        + 'the version that is live now. Leave anyway?',
        undefined, 'Keep editing', 'Leave', true);
      if (!confirmed) return;
    }

    this.selectedForm = null;
    this.builderSchema = null;
    this.builderDirty = false;
    this.importedDraftNotice = null;
    this.imports = [];
    this.versions = [];
    this.links = [];
    this.viewedVersion = null;
    this.viewedSchema = null;
    this.settingsForm = this.buildSettingsForm(null);
  }

  onBuilderDirtyChange(dirty: boolean): void {
    this.builderDirty = dirty;
  }

  /** A publish changed both the version list and the form's status — reload both. */
  onPublished(event: { version: CustomFormVersionDto; form: CustomFormDto }): void {
    if (!this.selectedForm) return;
    this.selectedForm = { ...this.selectedForm, ...event.form, currentVersion: event.version };
    this.settingsForm.get('status')!.setValue(event.form.status, { emitEvent: false });
    this.builderDirty = false;
    // These questions are a version now, so "unreviewed draft, not published" has stopped being
    // true and the banner that says it goes.
    this.importedDraftNotice = null;
    this.loadVersions(this.selectedForm.id);
    this.loadForms();
  }

  /* ── Delivery settings ────────────────────────────────────────────────────────────────── */

  private buildSettingsForm(form: CustomFormDto | null): FormGroup {
    return this.formBuilder.group({
      name: [form ? form.name : '', [Validators.required, Validators.maxLength(300)]],
      description: [form ? form.description || '' : ''],
      category: [form ? form.category || '' : ''],
      status: [form ? form.status : 'draft'],
      sendWithInvite: [form ? form.sendWithInvite : false],
      allowManualSend: [form ? form.allowManualSend : true],
      requiresSignature: [form ? form.requiresSignature : false],
      guardianMayComplete: [form ? !!form.guardianMayComplete : false],
      // null is meaningful: "ask once ever" rather than "re-ask every time" (§3.1). The controller
      // refuses 0 for exactly that reason.
      expiryDays: [form ? form.expiryDays : 30, [Validators.min(1), Validators.max(365)]],
      active: [form ? form.active : true],
    });
  }

  saveSettings(): void {
    if (!this.selectedForm) return;
    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      this.showToastMessage('Check the highlighted fields.');
      return;
    }

    const value = this.settingsForm.value;

    /*
     * A typed metadata object, NOT the form value spread.
     *
     * `CustomFormService.updateForm` takes `CustomFormMetadata`, which has no `schema`, no `groupId`
     * and no `currentVersionId` — each of which the controller refuses by name (§14.1 decision 4).
     * Naming the fields here means a control added to this form later cannot accidentally become a
     * field in the request body.
     */
    const metadata: CustomFormMetadata = {
      name: String(value.name || '').trim(),
      description: value.description ? String(value.description) : null,
      category: value.category ? String(value.category).trim() : null,
      status: value.status,
      sendWithInvite: !!value.sendWithInvite,
      allowManualSend: !!value.allowManualSend,
      requiresSignature: !!value.requiresSignature,
      guardianMayComplete: !!value.guardianMayComplete,
      expiryDays: this.numberOrNull(value.expiryDays),
      active: !!value.active,
    };

    this.isSavingSettings = true;
    this.customFormService.updateForm(this.selectedForm.id, metadata)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSavingSettings = false;
          this.selectedForm = { ...this.selectedForm!, ...response.data };
          this.showToastMessage('Delivery settings saved.');
          this.loadForms();
        },
        error: (error) => {
          this.isSavingSettings = false;
          const api = CustomFormService.asApiError(error);
          if (api && api.error === 'NOTHING_PUBLISHED') {
            // §14.1 decision 3 — the one refusal a practice is most likely to hit here, and the fix
            // is on the same screen.
            this.settingsForm.get('status')!.setValue(this.selectedForm!.status, { emitEvent: false });
            this.showToastMessage(
              'This form has no published version yet, so it cannot be marked published. Publish '
              + 'the questions below first.');
            return;
          }
          this.showToastMessage(this.messageFor(error, 'The settings could not be saved.'));
        },
      });
  }

  /* ── Version history ──────────────────────────────────────────────────────────────────── */

  loadVersions(id: string): void {
    this.isLoadingVersions = true;
    this.customFormService.listVersions(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.versions = response.data || [];
          this.currentVersionId = response.currentVersionId;
          this.isLoadingVersions = false;
        },
        error: () => {
          this.versions = [];
          this.isLoadingVersions = false;
        },
      });
  }

  /**
   * Open one version, read-only.
   *
   * §14.1 decision 2: the history rows carry no `schema`, so this is a second request. That is the
   * point — reading an old questionnaire is a deliberate act rather than a side effect of opening
   * the page.
   */
  viewVersion(version: CustomFormVersionDto): void {
    this.isLoadingVersion = true;
    this.viewedVersion = version;
    this.viewedSchema = null;

    this.customFormService.getVersion(version.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.viewedSchema = response.data.schema || null;
          this.isLoadingVersion = false;
        },
        error: (error) => {
          this.isLoadingVersion = false;
          this.viewedVersion = null;
          this.showToastMessage(this.messageFor(error, 'That version could not be opened.'));
        },
      });
  }

  closeVersion(): void {
    this.viewedVersion = null;
    this.viewedSchema = null;
  }

  /**
   * Load an old version's questions back into the builder.
   *
   * Explicitly NOT a "restore": the version itself is immutable and untouched. This copies its
   * questions into the draft being edited, and the practice still has to publish, which creates a
   * NEW version with a new number. The confirmation says so.
   */
  async copyVersionIntoBuilder(version: CustomFormVersionDto): Promise<void> {
    if (!this.selectedForm) return;

    const confirmed = await this.showConfirmationDialog(
      `Copy version ${version.versionNumber} into the editor`,
      `Version ${version.versionNumber} itself is never changed. Its questions are copied into the `
      + 'editor below, replacing what is there now; publishing then creates a new version with a '
      + 'new number.',
      undefined, 'Cancel', 'Copy into editor');
    if (!confirmed) return;

    this.customFormService.getVersion(version.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.builderSchema = response.data.schema || null;
          // The builder no longer holds the extraction, so its provenance banner must go with it.
          this.importedDraftNotice = null;
          this.closeVersion();
          this.showToastMessage(
            `Version ${version.versionNumber} copied into the editor. Publish to create a new version.`);
        },
        error: (error) => {
          this.showToastMessage(this.messageFor(error, 'That version could not be copied.'));
        },
      });
  }

  isCurrentVersion(version: CustomFormVersionDto): boolean {
    return !!this.currentVersionId && version.id === this.currentVersionId;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Import from a PDF (§16)
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * WHERE THE IMPORT IS OFFERED, AND WHY IT IS NOT OFFERED EVERYWHERE.
   *
   * Only on a draft that has never been published. §16.2 rule 1 says an import produces a draft for
   * a human to review — so the thing it lands in has to be a draft. On a form with a published
   * version, loading a machine extraction into the editor would put an unread questionnaire one
   * press away from replacing the one patients are answering today, and the practice's own copy of
   * those questions is what would be lost. Duplicating the form or creating a new one takes two
   * clicks and cannot do that, and the UI says so where the button would have been.
   */
  get canImportFromPdf(): boolean {
    return !!this.selectedForm
      && this.selectedForm.status === 'draft'
      && !this.selectedForm.currentVersionId;
  }

  loadImports(id: string): void {
    this.isLoadingImports = true;
    this.customFormService.listImports(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.imports = response.data || [];
          this.isLoadingImports = false;
        },
        error: () => {
          // The endpoint is additive to a settings page that worked without it — a form whose
          // history cannot be read still edits and still publishes.
          this.imports = [];
          this.isLoadingImports = false;
        },
      });
  }

  /**
   * Open the upload dialog.
   *
   * What comes back is at most a SCHEMA. The dialog cannot publish and cannot save; the only thing
   * a result does is put questions into the builder with a banner over them.
   */
  openImportDialog(): void {
    if (!this.selectedForm || !this.canImportFromPdf) return;

    const data: CustomFormImportDialogData = { form: this.selectedForm };
    const ref = this.dialog.open(CustomFormImportDialogComponent, {
      width: '680px',
      maxWidth: '96vw',
      autoFocus: false,
      data,
    });

    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: CustomFormImportDialogResult | undefined) => {
        // Whatever happened — reviewed, left running, failed — the attempt is a row now.
        if (this.selectedForm) this.loadImports(this.selectedForm.id);
        if (result && result.action === 'review') this.applyImportedDraft(result);
      });
  }

  /**
   * Put an extracted draft into the builder — the REAL builder, with a banner over it.
   *
   * `builderDirty` is set because that is exactly what these questions are: unpublished work the
   * practice would lose by walking away, and `closeEditor` asks before it lets them.
   */
  private applyImportedDraft(result: CustomFormImportDialogResult): void {
    this.builderSchema = result.schema;
    this.importedDraftNotice = {
      importId: result.importId,
      sourceFileName: result.sourceFileName,
      sourceFileUrl: result.sourceFileUrl,
      pageCount: result.pageCount,
      schemaErrors: result.schemaErrors || [],
    };
    this.builderDirty = true;
    this.showToastMessage(
      'The extracted questions are in the editor below. Nothing is published until you publish it.');
  }

  /**
   * Open a finished import from the history.
   *
   * The history rows carry no schema (the same reason §14.1 decision 2 keeps schemas off the
   * version list), so this reads the one import the practice asked for.
   */
  reviewImport(entry: CustomFormImportDto): void {
    if (!this.selectedForm || !this.canReviewImport(entry)) return;

    this.customFormService.getImport(entry.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const schema = response.data.schema || null;
          if (!schema) {
            this.showToastMessage(
              'That import finished without any questions in it. Try importing the document again.');
            return;
          }
          this.applyImportedDraft({
            action: 'review',
            importId: response.data.id,
            schema,
            sourceFileName: response.data.sourceFileName,
            sourceFileUrl: response.data.sourceFileUrl,
            pageCount: response.data.pageCount,
            schemaErrors: CustomFormService.importSchemaErrors(response.data),
          });
        },
        error: (error) => {
          this.showToastMessage(this.messageFor(error, 'That import could not be opened.'));
        },
      });
  }

  /**
   * An attempt can be opened whenever it HAS a draft, which is not the same as having succeeded.
   *
   * §16.2 rule 2: an extraction `validateSchema` refuses is stored anyway, as a `failed` row that
   * still carries its schema, so the practice can repair it rather than pay for the pages twice.
   * Gating this on `status === 'completed'` would throw that away — and it is the ordinary outcome
   * for a real scan, not an edge case.
   */
  canReviewImport(entry: CustomFormImportDto): boolean {
    if (!this.canImportFromPdf) return false;
    // `completed` means there is a schema by definition (§16.3), whether or not the list row
    // happens to advertise one; `failed` has to say so, and does.
    return entry.status === 'completed' || CustomFormService.hasRepairableSchema(entry);
  }

  importStatusLabel(entry: CustomFormImportDto): string {
    if (entry.status === 'pending') return 'Queued';
    if (entry.status === 'processing') return 'Being read';
    if (entry.status === 'completed') return 'Read — needs review';
    // A failure that kept its draft is a different fact from one that has nothing to show.
    return CustomFormService.hasRepairableSchema(entry) ? 'Read — needs repair' : 'Failed';
  }

  /* ── Appointment-type triggers ────────────────────────────────────────────────────────── */

  private loadAppointmentTypes(): void {
    if (!this.selectedGroup) return;
    this.appointmentTypeService.getAppointmentTypesByGroup(this.selectedGroup.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.appointmentTypes = Array.isArray(response) ? response : (response?.data || []);
        },
        error: () => {
          this.appointmentTypes = [];
        },
      });
  }

  loadLinks(id: string): void {
    this.customFormService.listAppointmentTypeLinks(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.links = response.data || [];
        },
        error: () => {
          this.links = [];
        },
      });
  }

  /**
   * LINK — upsert-shaped (§14.1 decision 8).
   *
   * 201 `created`, or 200 with `reactivated` true or false. All three are successes and each says
   * something different: a paused link still occupies the unique slot, so "already linked" is what a
   * practice sees when they link something they had paused, and telling them it was REACTIVATED is
   * the difference between the trigger firing and them thinking it will.
   */
  linkTrigger(): void {
    if (!this.selectedForm || !this.linkAppointmentTypeId) return;

    this.isLinking = true;
    this.customFormService
      .linkAppointmentType(this.selectedForm.id, this.linkAppointmentTypeId, this.linkSendOn)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLinking = false;
          this.linkAppointmentTypeId = null;
          this.showToastMessage(
            response.created ? 'Trigger added.'
              : response.reactivated ? 'That trigger was paused and has been switched back on.'
                : 'That trigger is already set up.');
          this.loadLinks(this.selectedForm!.id);
          this.loadForms();
        },
        error: (error) => {
          this.isLinking = false;
          this.showToastMessage(this.messageFor(error, 'That trigger could not be added.'));
        },
      });
  }

  /**
   * UNLINK — with `sendOn`, always.
   *
   * §14.1 decision 1: the endpoint's `?sendOn=` is optional and its absence removes BOTH triggers for
   * the (appointment type, form) pair. This list shows booking and reminder as separate rows, so
   * removing one row must remove one row. The service signature makes the parameter mandatory.
   */
  async unlinkTrigger(link: CustomFormLinkDto): Promise<void> {
    if (!this.selectedForm) return;

    const typeName = link.appointmentType ? link.appointmentType.type : 'that appointment type';
    const when = link.sendOn === 'reminder' ? 'with the reminder' : 'when it is booked';
    const confirmed = await this.showConfirmationDialog(
      'Remove trigger',
      `Stop sending "${this.selectedForm.name}" for ${typeName} ${when}? Other triggers on this `
      + 'form are not affected.');
    if (!confirmed) return;

    this.customFormService
      .unlinkAppointmentType(this.selectedForm.id, link.appointmentTypeId, link.sendOn)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showToastMessage('Trigger removed.');
          this.loadLinks(this.selectedForm!.id);
          this.loadForms();
        },
        error: (error) => {
          this.showToastMessage(this.messageFor(error, 'That trigger could not be removed.'));
        },
      });
  }

  /** Types not yet linked at the currently selected `sendOn`. A pair can hold one row per sendOn. */
  availableAppointmentTypes(): any[] {
    const taken = this.links
      .filter((link) => link.sendOn === this.linkSendOn)
      .map((link) => link.appointmentTypeId);
    return this.appointmentTypes.filter((type) => taken.indexOf(type.id) === -1);
  }

  sendOnLabel(sendOn: CustomFormSendOn): string {
    const found = this.sendOns.filter((s) => s.value === sendOn)[0];
    return found ? found.label : sendOn;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Helpers
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  statusLabel(status: CustomFormStatus): string {
    const found = this.statuses.filter((s) => s.value === status)[0];
    return found ? found.label : status;
  }

  trackByFormId(index: number, row: CustomFormRow): string {
    return row.form.id;
  }

  trackById(index: number, item: { id: string }): string {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private numberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }

  /** The server's own words where it gave any; a plain sentence where it did not. */
  private messageFor(error: any, fallback: string): string {
    const api = CustomFormService.asApiError(error);
    return api && api.message ? api.message : fallback;
  }
}
